import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { ShieldAlert, Banknote, ArrowLeftRight, Car, Loader2, RefreshCw } from "lucide-react";

const fmtTL = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (v) => (v ? String(v).slice(0, 16).replace("T", " ") : "—");

const LEVEL = {
  high: { label: "YÜKSEK RİSK", cls: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "ORTA RİSK", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { label: "DÜŞÜK RİSK", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

function StatCard({ icon: Icon, title, value, sub, tone, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-medium text-slate-500"><Icon className={`h-4 w-4 ${tone}`} /> {title}</div>
      <div className="mt-2 text-2xl font-bold text-slate-800">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function Empty({ text }) {
  return <p className="py-8 text-center text-sm text-slate-400">{text}</p>;
}

export default function RiskRadar() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/risk-radar", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const lvl = LEVEL[data?.level] || LEVEL.low;

  return (
    <div className="space-y-6" data-testid="risk-radar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
            <ShieldAlert className="h-6 w-6 text-brand-green" /> Risk Radar
          </h1>
          <p className="text-sm text-slate-500">Kasa farkları, transfer eksik teslim ve yetkisiz araç denemeleri tek panoda.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="risk-days" value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value={7}>Son 7 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
          </select>
          <button onClick={load} data-testid="risk-refresh" className="rounded-md border border-slate-300 bg-white p-2 text-slate-500 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !data ? (
        <Empty text="Veri yüklenemedi." />
      ) : (
        <>
          <div className="flex items-center gap-3" data-testid="risk-level">
            <span className={`rounded-full border px-3 py-1 text-sm font-semibold ${lvl.cls}`}>{lvl.label}</span>
            <span className="text-sm text-slate-500">Risk skoru: <b className="text-slate-700">{data.risk_score}</b> · son {data.days} gün</span>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard testid="risk-stat-cash" icon={Banknote} tone="text-red-500" title="Kasa Farkı Olayı"
              value={data.cash.count} sub={`Açık ${fmtTL(data.cash.shortage_total)} · Fazla ${fmtTL(data.cash.overage_total)}`} />
            <StatCard testid="risk-stat-transfer" icon={ArrowLeftRight} tone="text-amber-500" title="Transfer Eksik Teslim"
              value={data.transfers.count} sub="Fark/kayıp içeren transfer" />
            <StatCard testid="risk-stat-vehicle" icon={Car} tone="text-purple-500" title="Yetkisiz Araç Denemesi"
              value={data.unauthorized_vehicle.count} sub="Şoför başka aracı seçmeye çalıştı" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="risk-cash-list">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">Kasa Farkları</div>
            <div className="divide-y divide-slate-50">
              {data.cash.events.length === 0 ? <Empty text="Dönemde kasa farkı yok — kasalar denk." /> :
                data.cash.events.map((e, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div><span className="font-medium text-slate-800">{e.who || "—"}</span> <span className="text-slate-400">· {e.where || "—"} · {e.label}</span></div>
                    <div className="flex items-center gap-3">
                      <span className={`font-semibold ${e.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtTL(e.amount)}</span>
                      <span className="text-xs text-slate-400">{dt(e.at)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="risk-transfer-list">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">Transfer Eksik Teslim</div>
            <div className="divide-y divide-slate-50">
              {data.transfers.events.length === 0 ? <Empty text="Dönemde eksik teslim edilen transfer yok." /> :
                data.transfers.events.map((e, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div><span className="font-medium text-slate-800">{e.transfer_no}</span> <span className="text-slate-400">· {e.from} → {e.to}{e.note ? ` · ${e.note}` : ""}</span></div>
                    <div className="flex items-center gap-3"><span className="text-xs text-slate-500">{e.by}</span><span className="text-xs text-slate-400">{dt(e.at)}</span></div>
                  </div>
                ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="risk-vehicle-list">
            <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">Yetkisiz Araç Kullanım Denemeleri</div>
            <div className="divide-y divide-slate-50">
              {data.unauthorized_vehicle.events.length === 0 ? <Empty text="Yetkisiz araç kullanım denemesi tespit edilmedi." /> :
                data.unauthorized_vehicle.events.map((e, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div><span className="font-medium text-slate-800">{e.name || e.who}</span> <span className="text-slate-400">· {e.who} · araç: {e.vehicle_plate || "—"}</span></div>
                    <span className="text-xs text-slate-400">{dt(e.at)}</span>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
